import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { PlayerView } from './components/PlayerView';
import ViewerView from './components/ViewerView';
import { CustomAuthenticator } from './components/CustomAuthenticator';
import { UserRole } from './types/user.types';
import { COGNITO_CONFIG, API_CONFIG } from './utils/constants';
import '@aws-amplify/ui-react/styles.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { Amplify } from 'aws-amplify';
import './components/Views.css';

Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: COGNITO_CONFIG.userPoolId,
            userPoolClientId: COGNITO_CONFIG.userPoolClientId
        }
    },
    API: {
        REST: {
            'demo-api': {
                endpoint: API_CONFIG.endpoint
            }
        }
    }
});

/**
 * Determines user role based on email address
 * @param email - User's email address
 * @returns UserRole - 'player' or 'viewer'
 */
function determineUserRole(email: string): UserRole {
    const normalizedEmail = email.toLowerCase().trim();
    
    // Check for player email
    if (normalizedEmail === 'player@ivs.rocks') {
        return 'player';
    }
    
    // Check for viewer email
    if (normalizedEmail === 'viewer@ivs.rocks') {
        return 'viewer';
    }
    
    // Default to viewer for unknown emails
    return 'viewer';
}

/**
 * Main content component that handles role-based routing
 */
interface MainContentProps {
    signOut?: () => void;
    user: any;
}

const MainContent: React.FC<MainContentProps> = ({ signOut, user }) => {
    const [userRole, setUserRole] = useState<UserRole | null>(null);

    useEffect(() => {
        if (user?.signInDetails?.loginId) {
            const role = determineUserRole(user.signInDetails.loginId);
            setUserRole(role);
        }
    }, [user]);

    // Show loading state while determining role
    if (!userRole) {
        return <div>Loading...</div>;
    }

    const isPlayer = userRole === 'player';

    return (
        <Router>
            <Routes>
                {/* Default route - role-based view */}
                <Route 
                    path="/" 
                    element={
                        isPlayer ? (
                            <PlayerView signOut={signOut} user={user} />
                        ) : (
                            <ViewerView signOut={signOut} user={user} />
                        )
                    } 
                />
            </Routes>
        </Router>
    );
};

function App() {
    return (
        <CustomAuthenticator>
            {({ signOut, user }) => (
                <MainContent signOut={signOut} user={user} />
            )}
        </CustomAuthenticator>
    );
}

export default App;
